import { useEffect, useState } from "react";
import { AccessibilityInfo, Platform } from "react-native";

/**
 * Custom hook for managing accessibility preferences
 * Provides access to user's accessibility settings and preferences
 *
 * A warning about `screenReaderEnabled` on the web build: react-native-web
 * hardcodes `AccessibilityInfo.isScreenReaderEnabled()` to resolve `true`,
 * because a browser gives it no way to detect a reader and it would rather
 * over-serve accessibility than under-serve it. So on web this flag is a
 * constant `true`, not a measurement.
 *
 * Use it only to turn extra help *on*, the way the announce helpers do. Never
 * use it to switch a feature *off* — that disables the feature for everybody.
 * This cost us a menu hint that silently never ran in any browser.
 *
 * `reduceMotion` is the real thing: it tracks the prefers-reduced-motion media
 * query, so it is the correct gate for anything animated.
 */
export const useAccessibility = () => {
  const [reduceMotion, setReduceMotion] = useState(false);
  const [screenReaderEnabled, setScreenReaderEnabled] = useState(false);
  const [highContrastEnabled, setHighContrastEnabled] = useState(false);
  const [boldTextEnabled, setBoldTextEnabled] = useState(false);

  useEffect(() => {
    // Check reduced motion preference
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);

    // Check screen reader status
    AccessibilityInfo.isScreenReaderEnabled().then(setScreenReaderEnabled);

    // Check high contrast preference (iOS only)
    if (Platform.OS === "ios") {
      AccessibilityInfo.isReduceTransparencyEnabled().then(
        setHighContrastEnabled,
      );
    }

    // Check bold text preference (iOS only)
    if (Platform.OS === "ios") {
      AccessibilityInfo.isBoldTextEnabled().then(setBoldTextEnabled);
    }

    if (typeof document !== "undefined") {
      const styleId = "outback-focus-visible-style";
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) existingStyle.remove();
    }

    // Set up listeners for changes
    const motionListener = AccessibilityInfo.addEventListener(
      "reduceMotionChanged",
      setReduceMotion,
    );

    const screenReaderListener = AccessibilityInfo.addEventListener(
      "screenReaderChanged",
      setScreenReaderEnabled,
    );

    const contrastListener = AccessibilityInfo.addEventListener(
      "reduceTransparencyChanged",
      setHighContrastEnabled,
    );

    const boldTextListener = AccessibilityInfo.addEventListener(
      "boldTextChanged",
      setBoldTextEnabled,
    );

    // Cleanup listeners
    return () => {
      motionListener?.remove();
      screenReaderListener?.remove();
      contrastListener?.remove();
      boldTextListener?.remove();
    };
  }, []);

  return {
    reduceMotion,
    screenReaderEnabled,
    highContrastEnabled,
    boldTextEnabled,
  };
};

/**
 * Utility function to get animation duration based on motion preferences
 * @param {number} defaultDuration - Default animation duration in ms
 * @param {boolean} reduceMotion - Whether user prefers reduced motion
 * @returns {number} Adjusted duration (0 if reduced motion is enabled)
 */
export const getAnimationDuration = (defaultDuration, reduceMotion) => {
  return reduceMotion ? 0 : defaultDuration;
};

/**
 * Utility function to get animation config based on motion preferences
 * @param {Object} config - Animation configuration object
 * @param {boolean} reduceMotion - Whether user prefers reduced motion
 * @returns {Object} Modified animation config
 */
export const getAnimationConfig = (config, reduceMotion) => {
  if (reduceMotion) {
    return {
      ...config,
      duration: 0,
      useNativeDriver: false, // Disable native driver for immediate animations
    };
  }
  return config;
};
